import { ActionIcon, AspectRatio, Box, Button, Flex, Grid, Image, Modal, Paper, Radio, Text, TextInput, Title, useComputedColorScheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useState } from "react";

import { select_opt1_ad_creative, select_opt2_ad_creative, select_opt2_ad_creative_dark, select_opt_design_ad_creative, preview_bf_billboard, preview_bf_medium_rectangle, preview_bf_halfpage } from "@/assets/img";
import { Link, router } from "@inertiajs/react";
import { mutateAdCreate, queryOptions } from "@/Services";
import { mutateDesignRequestCreate } from "@/Services/studio-design-request";
import { BriefingAdvertiserField } from "@/Partials/Studio/DesignRequest";
import { notifications } from "@mantine/notifications";

import { radioCardClasses } from "@/ui-temp";
import { useTranslation } from "react-i18next";
import classes from "./create-ad-button.module.css";

// Modal steps: pick a way, then name the ad and pick its format(s).
const STEP_CHOOSE = 'choose';
const STEP_UPLOAD = 'upload';
// AD-220: "Let Us Design It" — one format for now (AD-225, answer 1).
const STEP_BRIEFING = 'briefing';
// AD-259: the briefing starts in German (adstudio.design_request.default_language), so the
// advertiser search orders German places first.
const BRIEFING_LANGUAGE = 'de';
// AD-220: the briefing's formats in the design's order, each with its size.
const BRIEFING_FORMAT_ORDER = ['medium_rectangle', 'billboard-970-250', 'halfpage'];

export const CreateAdButton = ({ compact = false }) => {

    const { t } = useTranslation();
    const isDark = useComputedColorScheme('light') === 'dark';

    const [opened, { open, close }] = useDisclosure(false);
    const [step, setStep] = useState(STEP_CHOOSE);
    const { data: formatData, isLoading: isLoadingFormat } = queryOptions({ type: 'format' });
    const mutation = mutateAdCreate();
    const designRequestMutation = mutateDesignRequestCreate();

    const [advertiserName, setAdvertiserName] = useState("");
    const [bannerFormat, setBannerFormat] = useState(null);
    // 422 messages of the briefing step, by field: the name's under the name.
    const [briefingErrors, setBriefingErrors] = useState({});

    const formatPreviews = {
        'billboard': preview_bf_billboard,
        'billboard-970-250': preview_bf_billboard,
        'medium_rectangle': preview_bf_medium_rectangle,
        'halfpage': preview_bf_halfpage,
    }

    // The 800 x 250 billboard is not offered for new ads.
    const formats = formatData?.filter(format => format.alias !== 'billboard') || [];
    const briefingFormats = [...formats].sort((a, b) => {
        const rank = (format) => {
            const index = BRIEFING_FORMAT_ORDER.indexOf(format.alias);
            return index === -1 ? BRIEFING_FORMAT_ORDER.length : index;
        };
        return rank(a) - rank(b);
    });

    const closeAndReset = () => {
        close();
        setStep(STEP_CHOOSE);
        setAdvertiserName("");
        setBriefingErrors({});
    }

    const initOpen = () => {
        setStep(STEP_CHOOSE);
        // The first format offered (the 800 x 250 billboard is not).
        setBannerFormat(formats[0]?.value ?? null);
        open();
    }

    const startBriefing = () => {
        setBannerFormat(briefingFormats[0] ? String(briefingFormats[0].value) : null);
        setStep(STEP_BRIEFING);
    }

    const submitData = () => {
        mutation.mutate({
            name: advertiserName,
            banner_ids: [bannerFormat],
            tech_type: 'image-1'
        }, {
            onSuccess: ({ data }) => {
                router.visit(route('studio.ad_builder.edit', { ad: data?.ad?.id }), {
                    preserveState: true,
                    replace: true
                });
            },
            onSettled: () => {
                closeAndReset();
            }
        });
    }

    const submitBriefingStart = () => {
        setBriefingErrors({});
        designRequestMutation.mutate({
            advertiser_name: advertiserName,
            banner_format_ids: [Number(bannerFormat)],
        }, {
            onSuccess: ({ data }) => {
                router.visit(route('studio.briefing.edit', { designRequest: data?.data?.id }));
                closeAndReset();
            },
            onError: (error) => {
                const errors = error?.response?.data?.errors || {};
                if (Object.keys(errors).length > 0) {
                    const formatError = Object.entries(errors).find(([field]) => field.startsWith('banner_format_ids'));
                    setBriefingErrors({
                        advertiser_name: errors.advertiser_name?.[0],
                        banner_format_ids: formatError?.[1]?.[0],
                    });
                    return;
                }
                notifications.show({ color: 'red', message: error?.response?.data?.message || t('briefing.errors.saveFailed') });
            },
        });
    }

    const formatLabel = (format) => format.label?.split(" (")[0];
    // "Medium Rectangle (300 x 250 px)" → "300x250".
    const formatSize = (format) => {
        const match = /\((\d+)\s*x\s*(\d+)/i.exec(format.label || '');
        return match ? `${match[1]}x${match[2]}` : null;
    };

    // The briefing shows each format's size (AD-220 design); delivery, and with it
    // where the ad can run, is set up only after the design is approved.
    const renderFormatPreview = (format, { withSize = false } = {}) => (
        <Box className={classes.formatPreview}>
            <Image src={formatPreviews[format.alias]} alt={formatLabel(format)} />
            <Text fw="400" className={classes.formatLabel}>
                {withSize ? t(`createAd.briefingFormats.${format.alias}`, formatLabel(format)) : formatLabel(format)}
                {withSize && formatSize(format) && <Text span inherit c="dimmed"> ({formatSize(format)})</Text>}
            </Text>
            {!withSize && format.smart_ads_available === false && (
                <Text ta="center" fw="400" fz={12} c="dimmed">{t('createAd.audienceNetworkOnly')}</Text>
            )}
        </Box>
    );

    const advertiserInput = (
        <TextInput
            label={t('adBuilder.advertiserName')}
            placeholder={t('createAd.namePlaceholder')}
            required mt="md"
            variant="filled"
            value={advertiserName}
            onChange={(event) => setAdvertiserName(event.currentTarget.value)}
        />
    );

    const wayCard = ({ media, title, text, action }) => (
        <Paper withBorder mih={200} h="100%" radius="md" className={classes.wayCard}>
            <Flex direction="column" align="center" gap={2} h="100%">
                <AspectRatio ratio={1 / 1} w="100%" mx="auto" className={classes.wayMedia}>
                    {media}
                </AspectRatio>
                <Text fw="600" fz={16} ta="center">{title}</Text>
                <Text fw="400" fz={14} color="dimmed" ta="center" lineClamp={3}>{text}</Text>
                <Box mt="auto" className={classes.wayAction}>{action}</Box>
            </Flex>
        </Paper>
    );

    return (
        <>
            <Modal opened={opened} onClose={closeAndReset} size={step === STEP_CHOOSE ? 960 : "xl"} padding="md" radius="lg" withCloseButton={false} classNames={{ inner: classes.inner, content: classes.content, body: classes.body }}>
                <Box p="md" pos="relative">
                    <ActionIcon onClick={closeAndReset} pos="absolute" top={{ base: 9, sm: 0 }} right={{ base: 9, sm: 0 }} variant="transparent" color="dark">
                        <IconX size={24} />
                    </ActionIcon>
                    {step === STEP_CHOOSE && (
                        <>
                            <Flex direction="column" align="center" gap={2} mb={16} className={classes.chooseHeader}>
                                <Title order={4} ta="center">{t('createAd.title')}</Title>
                                <Text color="dimmed" ta="center" className={classes.subtitle}>{t('createAd.subtitle')}</Text>
                            </Flex>

                            <Grid>
                                <Grid.Col span={{ base: 12, sm: 4 }}>
                                    {wayCard({
                                        media: <Image src={select_opt_design_ad_creative} alt="Let Us Design It" />,
                                        title: t('createAd.designIt'),
                                        text: t('createAd.designItText'),
                                        action: (
                                            <Button onClick={startBriefing} size="xs" disabled={isLoadingFormat}>
                                                {t('createAd.startBriefing')}
                                            </Button>
                                        ),
                                    })}
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 4 }}>
                                    {wayCard({
                                        media: <Image src={select_opt1_ad_creative} alt="Template Option" />,
                                        title: t('createAd.withTemplate'),
                                        text: t('createAd.withTemplateText'),
                                        action: (
                                            <Button component={Link} href={route('studio.studio_editor_v2.create')} size="xs">
                                                {t('createAd.browseTemplates')}
                                            </Button>
                                        ),
                                    })}
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 4 }}>
                                    {wayCard({
                                        media: <Image src={isDark ? select_opt2_ad_creative_dark : select_opt2_ad_creative} alt="Upload Option" />,
                                        title: t('createAd.uploadCreative'),
                                        text: t('createAd.uploadCreativeText'),
                                        action: (
                                            <Button onClick={() => setStep(STEP_UPLOAD)} size="xs">
                                                {t('createAd.uploadButton')}
                                            </Button>
                                        ),
                                    })}
                                </Grid.Col>
                            </Grid>
                        </>
                    )}
                    {step === STEP_UPLOAD &&
                        (
                            <>
                                <Title order={4}>{t('createAd.customTitle')}</Title>
                                {advertiserInput}

                                <Radio.Group
                                    value={bannerFormat}
                                    onChange={setBannerFormat}
                                    label={t('common.format')}
                                    classNames={{ root: classes.formatGroup, description: classes.formatHelp }}
                                >
                                    <Grid gutter={{ base: 8, sm: 'md' }}>
                                        {
                                            isLoadingFormat ? <Text>{t('createAd.loadingFormats')}</Text> : formats.map(format => (
                                                <Grid.Col span={4} key={format.value}>
                                                    <Radio.Card radius="md" value={format.value} className={radioCardClasses.root}>
                                                        {renderFormatPreview(format)}
                                                    </Radio.Card>
                                                </Grid.Col>
                                            ))
                                        }
                                    </Grid>
                                </Radio.Group>

                                <Flex justify="flex-end" className={classes.footer}>
                                    <Button onClick={closeAndReset} variant="default">{t('common.cancel')}</Button>
                                    <Button onClick={submitData} disabled={advertiserName.length < 1} loading={mutation.isPending}>{t('createAd.customTitle')}</Button>
                                </Flex>
                            </>
                        )}
                    {step === STEP_BRIEFING &&
                        (
                            <>
                                <Title order={4}>{t('createAd.briefingTitle')}</Title>
                                <Box mt="md">
                                    <BriefingAdvertiserField
                                        value={advertiserName}
                                        onChange={setAdvertiserName}
                                        language={BRIEFING_LANGUAGE}
                                        label={t('adBuilder.advertiserName')}
                                        placeholder={t('createAd.briefingNamePlaceholder')}
                                        error={briefingErrors.advertiser_name}
                                    />
                                </Box>

                                <Radio.Group
                                    value={bannerFormat}
                                    onChange={setBannerFormat}
                                    label={t('createAd.formatsQuestion')}
                                    description={t('createAd.formatsHelp')}
                                    mt="md"
                                    error={briefingErrors.banner_format_ids}
                                    classNames={{ root: classes.formatGroup, description: classes.formatHelp }}
                                >
                                    <Grid mt="xs" gutter={{ base: 8, sm: 'md' }}>
                                        {
                                            isLoadingFormat ? <Text>{t('createAd.loadingFormats')}</Text> : briefingFormats.map(format => (
                                                <Grid.Col span={4} key={format.value}>
                                                    <Radio.Card radius="md" value={String(format.value)} className={radioCardClasses.root}>
                                                        {renderFormatPreview(format, { withSize: true })}
                                                    </Radio.Card>
                                                </Grid.Col>
                                            ))
                                        }
                                    </Grid>
                                </Radio.Group>

                                <Flex justify="flex-end" className={classes.footer}>
                                    <Button onClick={closeAndReset} variant="default">{t('common.cancel')}</Button>
                                    <Button
                                        onClick={submitBriefingStart}
                                        disabled={advertiserName.trim().length < 1 || !bannerFormat}
                                        loading={designRequestMutation.isPending}
                                    >
                                        {t('createAd.customTitle')}
                                    </Button>
                                </Flex>
                            </>
                        )}
                </Box>
            </Modal>
            {compact ? (
                <ActionIcon color="pink.7" size={36} radius={8} onClick={initOpen} aria-label={t('createAd.button')} className={classes.compactTrigger}>
                    <IconPlus size={16} />
                </ActionIcon>
            ) : (
                <Button leftSection={<IconPlus size={20} />} onClick={initOpen}>{t('createAd.button')}</Button>
            )}
        </>
    )
}
